<template>
  <div>
    <div class="flex-column gap-2xl" v-if="order">
      <div v-if="$slots['title'] || showTitle">
        <slot name="title">
          <h2 class="margin-remove">
            {{
              tu("ecommerce.order.adminSummary.title", { orderId: order?.id })
            }}
          </h2>
          <h4 class="text-normal">
            <span>{{ t(`ecommerce.order.fields.status`) }}:</span>&nbsp;<span class="bold">{{ t(`ecommerce.order.fields.statuses.${order?.status}`) }}</span>
          </h4>
        </slot>
      </div>

      <card title="ecommerce.order.admin.quoteWithExplain">
        <cart
          :quote="order.Quote"
          :read-only="true"
          :show-action-bar="false"></cart>
      </card>

      <card>
        <div class="flex-column l:flex-row width-expand gap-l">
          <div class="buyer-details width-expand">
            <h3 class="margin-m-bottom">
              {{ tu("ecommerce.order.adminSummary.buyerDetails") }}
            </h3>
            <simple-value-list :list="buyerInformationList"></simple-value-list>
          </div>
          <div class="vendor-details width-expand">
            <h3 class="margin-m-bottom">
              {{ tu("ecommerce.order.adminSummary.vendorDetails") }}
            </h3>
            <simple-value-list
              :list="vendorInformationList"></simple-value-list>
          </div>
        </div>
      </card>

      <card title="">
        <template #title>
          <div>
            <span class="block margin-xs-bottom margin-remove">{{
              tu("ecommerce.order.adminSummary.paymentDetails")
            }}</span>
            <span class="block text-m bold" v-if="order.Payments && order.Payments.length > 0">
              {{
                translate(
                  "ecommerce.order.fields.orderPaymentSummaryText",
                  getPaymentSummaryForOrder(order)
                )
              }}</span
            >
          </div>
        </template>
        <template #default>
          <div v-if="!order.Payments || order.Payments.length < 1">
            <h5>{{ tu("ecommerce.order.adminSummary.noPayments") }}</h5>
          </div>
          <div v-else>
            <table class="table table--tight">
              <thead>
                <tr>
                  <th>{{ tu("core.generalFields.id.label") }}</th>
                  <th>{{ tu("core.generalFields.uuid.shortLabel") }}</th>
                  <th>{{ tu("payments.fields.amount") }}</th>
                  <th>{{ tu("payments.fields.currency") }}</th>
                  <th>{{ tu("payments.fields.handler") }}</th>
                  <th>{{ tu("payments.fields.handlerId") }}</th>
                  <th>{{ tu("payments.fields.status") }}</th>
                  <th>
                    {{ tu("ecommerce.order.adminSummary.paymentActions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment of order.Payments">
                  <td>{{ payment.id }}</td>
                  <td>
                    <span
                      v-tooltip="{
                        content: payment.uuid,
                        autoTranslate: false,
                      }"
                      class="block text-overflow-ellipsis max-width-100 cursor-pointer">
                      {{ payment.uuid }}
                    </span>
                  </td>
                  <td>
                    {{
                      translateNumber(payment.amount, "currency", {
                        currency: payment.currency,
                      })
                    }}
                  </td>
                  <td>
                    {{ payment.currency }}
                  </td>
                  <td>
                    {{ payment.handler }}
                  </td>
                  <td>
                    {{
                      payment.handlerId && payment.handlerId != ""
                        ? payment.handlerId
                        : "-"
                    }}
                  </td>
                  <td>
                    {{ t(`payments.status.${payment.status}`) }}
                  </td>
                  <td >
                    <div class="flex gap-m">

                      <form-button

                        v-if="$acl.can('payment','update',['status']) && payment.status !== 'completed'"
                        icon="check"
                        theme="success"
                        :slim="true"
                        :square="true"
                        v-tooltip="'ecommerce.order.adminSummary.paymentActionStatusToComplete'"
                        @click="updatePayment(payment.id, {status: 'completed', 'handlerId': 'MANUAL'})"

                        :text="false"></form-button>
                      <form-button
                        v-if="$acl.can('payment','update',['status']) &&  payment.status === 'completed'"
                        icon="x"
                        theme="danger"
                        :slim="true"
                        :square="true"
                        v-tooltip="'ecommerce.order.adminSummary.paymentActionStatusToCanceled'"
                        @click="updatePayment(payment.id, {status: 'canceled', 'handlerId': null})"
                        :text="false"></form-button>
                    </div>

                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </card>
    </div>
  </div>
</template>

<script>
import Cart from "@/client/components/ecommerce/Cart.vue";
import SimpleValueList from "@/client/components/base/frameworks/we/etc/SimpleValueList.vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { toRaw } from 'vue';

export default {
  name: "AdminOrderSummary",
  components: {
    Cart,
    SimpleValueList,
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },
    modelValue:  { type: Object, required: true },
    buyerFields: {
      type: Array,
      default: [
        "id",
        "fullName",
        "email",
        "phoneNumber",
        "uuid",
        "userGroups",
        "locale",
        "state",
      ],
    },
    vendorFields: {
      type: Array,
      default: ["id", "name", "state"],
    },
  },
  emits: ['adminOrderSummary:updated'],
  data: function () {
    return {
      userForm: false,
      paymentForm: false,
      vendorForm: false,
      order: structuredClone(toRaw(this.modelValue)),     // deep clone
      asyncData: {
        userForm: "entity/user/update",
        vendorForm: "entity/vendor/update",
        paymentForm: "entity/payment/update",
      },
    };
  },
  computed: {
    orderVendor() {
      return this.order?.Vendors[0];
    },
    buyerInformationList() {
      const res = [];
      if (!this.userForm) {
        return res;
      }

      Object.entries(this.userForm.fields).forEach(([key, field]) => {
        if (!this.buyerFields.includes(key)) {
          return true;
        }
        let label;

        if (field.placeholder) {
          label = field.placeholder;
        }
        if (field.label) {
          label = field.label;
        }
        res.push({ label: label, value: this?.order?.Buyer[key] });
      });
      return res;
    },
    vendorInformationList() {
      const res = [];
      if (!this.vendorForm) {
        return res;
      }


      Object.entries(this.vendorForm.fields).forEach(([key, field]) => {
        if (!this.vendorFields.includes(key)) {
          return true;
        }
        let label;

        if (field.placeholder) {
          label = field.placeholder;
        }
        if (field.label) {
          label = field.label;
        }
        res.push({ label: label, value: this?.order?.Vendors?.[0]?.[key] });
      });

      res.push({
        label: "ecommerce.order.adminSummary.vendorUserFullName",
        value: this?.order?.Vendors?.[0]?.Owner?.fullName,
      });
      res.push({
        label: "ecommerce.order.adminSummary.vendorUserEmail",
        value: this?.order?.Vendors?.[0]?.Owner?.email,
      });
      res.push({
        label: "ecommerce.order.adminSummary.vendorUserPhoneNumber",
        value: this?.order?.Vendors?.[0]?.Owner?.phoneNumber,
      });
      return res;
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        Object.assign(this.order, structuredClone(toRaw(newVal)));
      },
      deep: true
    }
  },
  methods: {
    updateModel() {
      this.$emit('update:modelValue', structuredClone(toRaw(this.order)));
    },
    getPaymentSummaryForOrder(order) {
      let res = {
        total: 0,
        count: 0,
        status: "-",
      };
      if (!order.Payments || order.Payments.length < 1) {
        return res;
      }
      res.status = this.t("ecommerce.order.paymentStatus.complete");
      order.Payments.forEach((payment) => {
        res.total = res.total + payment.amount;

        if (payment.status !== "completed") {
          res.status = this.t("ecommerce.order.paymentStatus.incomplete");
        }
      });

      res.total = this.translateNumber(res.total, "currency", {
        currency: order.Quote.currency,
      });
      return res;
    },
    async updatePayment(paymentId, updates = {}) {
      let url = `entity/payment/${paymentId}`;
      let callResult = await this.asyncOps.patch(url, updates);
      // find the payment in the order.
      let payment = this.order.Payments.filter(item => item.id === paymentId)[0];
      payment.status = callResult.data.status;
      this.$emit('adminOrderSummary:updated');

      this.updateModel();
      
    }
  },
};
</script>

<style lang="scss" scoped>
.details-field {
  display: block;
}

#buyer-seller-details {
  justify-content: space-evenly;
}

.seller-logo {
  width: calc(var(--font-max) * 2);
  height: calc(var(--font-max) * 2);
  display: block;
}

.with-border {
  border: 1px solid var(--c-gray-4) !important;
}

table {
  tr:nth-child(2) {
    border-top: 1px solid var(--c-gray-2);
  }
}

caption {
  color: var(--c-alt);
  margin-bottom: var(--margin-m);
  margin-top: var(--margin-m);
}
</style>
